// Native-WebSocket wrapper for the wt-wsgw gateway. Owns:
//   - one persistent connection per instance,
//   - a subscription registry (channel → set<handler>),
//   - exponential-backoff reconnect (1s → 30s) on unexpected close,
//   - replay of every active subscribe on (re)open.
//
// Routing is intentionally trivial: the server tags every outbound
// frame with `channel`, so we look it up directly. We intentionally
// don't try to reverse-derive channel from event+data — the old plan
// did that and it was brittle (pair-update vs trade for the same
// pool addr disagree on which channels to deliver to).

export type Channel =
  | 'discover'
  | 'pulse'
  | 'trades'
  | `pair:${string}`
  | `pair:${string}:trades`
  // The pool's server-built Top traders rows (`toptraders.row`)
  | `pair:${string}:toptraders`
  | `wallet:${string}`
  // A token's holder balances from our journal, pushed per settlement
  // (`holders.journal`; wiki/pr/JOURNAL-REALTIME-PUSH-20261102.md)
  | `holders:${string}`
  // A token's holder figures, pushed after a balance change (`token.stats`)
  | `token:${string}:stats`
  // One owner's token balances from our journal (`jettons.balance`)
  | `jettons:${string}`
  | `tracker:${string}`
  // Ranked-board membership, one channel per (sort, window) so every
  // subscriber watching the same board shares one server-side evaluation.
  | `board:${string}`

export type WsEvent =
  | 'discover.tick'
  | 'pair.update'
  | 'trade'
  | 'liquidity'
  | 'pair.activity'
  | 'launchpad.deploy'
  | 'launchpad.progress'
  | 'launchpad.migrate'
  | 'pulse.aggregate'
  | 'token.discovered'
  | 'token.metadata'
  | 'token.valuation'
  | 'token.profile'
  | 'board.enter'
  | 'board.leave'
  | 'wallet.balance'
  | 'wallet.confirmation'
  | 'wallet.activity'
  | 'holders.journal'
  // The server-built Holders / Top traders row (holders:<master>,
  // pair:<pool>:toptraders; wiki/plans/2026-11-04-realtime-stage1-design.md)
  | 'holders.row'
  | 'toptraders.row'
  // A token's holder figures (token:<master>:stats; lib/tokenStats)
  | 'token.stats'
  | 'jettons.balance'
  // The account's rewards moved (tracker channel; a trigger, no figures).
  | 'rewards.update'
  | 'ack'
  | 'pong'

export interface WsMessage<T = unknown> {
  event: WsEvent
  channel?: string
  data: T
}

export type WsHandler<T = unknown> = (msg: WsMessage<T>) => void

interface ClientOp {
  op: 'subscribe' | 'unsubscribe' | 'ping'
  channel?: string
}

type PendingChannelOp = {
  op: 'subscribe' | 'unsubscribe'
  generation: number
}

type SubscriptionWaiter = {
  channel: string
  generation: number
  resolve: () => void
  reject: (error: Error) => void
}

export type WsStatus = 'connecting' | 'open' | 'closed'

const INITIAL_BACKOFF_MS = 1000
const MAX_BACKOFF_MS = 30_000
// How long a resumed socket has to prove it is still connected before it is
// replaced. Long enough for a mobile round-trip on a waking radio, short enough
// that the user does not sit in front of a dead tape.
const RESUME_PROBE_MS = 2500

export class WsClient {
  private url: string
  private ws: WebSocket | null = null
  private subscriptions: Map<string, Set<WsHandler>> = new Map()
  private backoffMs: number = INITIAL_BACKOFF_MS
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private livenessTimer: ReturnType<typeof setTimeout> | null = null
  // Every inbound frame bumps this. resumeNow's probe compares it before and
  // after to decide whether a socket that CLAIMS to be open really is.
  private inboundCount = 0
  private destroyed = false
  // Connection state for the optional indicator + the reconnect re-seed
  // (FRONTEND-REALTIME). `hasOpened` gates onReconnect so the FIRST open
  // (initial connect, whose REST seed already ran) does not fire a redundant
  // re-seed; every later open is a true reconnect that must catch the gap.
  private status: WsStatus = 'closed'
  private hasOpened = false
  private reconnectHandlers: Set<() => void> = new Set()
  private statusHandlers: Set<(s: WsStatus) => void> = new Set()
  private connectionGeneration = 0
  private acknowledgedSubscriptions: Map<string, number> = new Map()
  private pendingChannelOps: Map<string, PendingChannelOp[]> = new Map()
  private subscriptionWaiters: Set<SubscriptionWaiter> = new Set()
  private pendingReconnect: { generation: number; channels: Set<string> } | null = null

  constructor(url: string) {
    this.url = url
  }

  connect(): void {
    if (this.destroyed) return
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return
    }
    this.setStatus('connecting')
    try {
      this.ws = new WebSocket(this.url)
    } catch (_err) {
      // URL invalid or sync throw — schedule a retry rather than
      // crashing the React tree.
      this.setStatus('closed')
      this.scheduleReconnect()
      return
    }
    this.ws.onopen = () => {
      this.connectionGeneration += 1
      const generation = this.connectionGeneration
      this.pendingChannelOps.clear()
      this.backoffMs = INITIAL_BACKOFF_MS
      this.setStatus('open')
      // Replay every active subscription so the server's per-connection
      // registry matches ours after a reconnect.
      for (const channel of this.subscriptions.keys()) {
        this.sendChannelOp({ op: 'subscribe', channel }, generation)
      }
      // Reconnect re-seeds are a correctness barrier: they fire only after the
      // server has ACKed every subscription replayed onto this connection.
      if (this.hasOpened) {
        this.pendingReconnect = {
          generation,
          channels: new Set(this.subscriptions.keys()),
        }
        this.maybeNotifyReconnect()
      } else {
        this.hasOpened = true
      }
    }
    this.ws.onmessage = (ev: MessageEvent) => {
      // Counted before parsing: a malformed frame is still proof that the peer
      // is answering, which is all the resume probe asks.
      this.inboundCount += 1
      let msg: WsMessage
      try {
        msg = JSON.parse(typeof ev.data === 'string' ? ev.data : String(ev.data)) as WsMessage
      } catch {
        return
      }
      this.route(msg)
    }
    this.ws.onerror = () => {
      // Browsers fire onclose right after onerror; let onclose handle reconnect.
    }
    this.ws.onclose = () => {
      this.ws = null
      this.setStatus('closed')
      if (this.destroyed) return
      this.scheduleReconnect()
    }
  }

  private setStatus(s: WsStatus): void {
    if (this.status === s) return
    this.status = s
    for (const h of this.statusHandlers) {
      try {
        h(s)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[WsClient] status handler threw', err)
      }
    }
  }

  getStatus(): WsStatus {
    return this.status
  }

  /** Terminal state after destroy(); sharedWsClient uses it to hand out a
      fresh client if the app root ever remounts after a real unmount. */
  get isDestroyed(): boolean {
    return this.destroyed
  }

  // onReconnect registers a handler fired once per RE-open (a socket that
  // dropped and came back) — never on the initial connect. Returns an
  // unsubscribe. Consumers use it to re-read the REST seed and catch the gap
  // of events missed while the socket was down (WS-primary, no continuous poll).
  onReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler)
    return () => {
      this.reconnectHandlers.delete(handler)
    }
  }

  // onStatusChange registers a connection-status listener for the optional
  // indicator. Returns an unsubscribe.
  onStatusChange(handler: (s: WsStatus) => void): () => void {
    this.statusHandlers.add(handler)
    return () => {
      this.statusHandlers.delete(handler)
    }
  }

  private scheduleReconnect(): void {
    if (this.destroyed || this.reconnectTimer) return
    const delay = this.backoffMs
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS)
      this.connect()
    }, delay)
  }

  // resumeNow is the "this tab is in front of a human again" signal (see
  // WsClient.resume.test.ts). A phone suspends the page, the radio sleeps and
  // the carrier drops the connection; two separate things then delayed the
  // first live trade by ~10s, and only a page reload cleared it:
  //
  //   - the backoff had already climbed toward MAX_BACKOFF_MS from the drops
  //     that happened while the tab was hidden, so the next retry was scheduled
  //     tens of seconds out. Coming back is evidence the network is usable, so
  //     the backoff earned while nobody was watching is not worth honouring.
  //   - the socket is frequently HALF-OPEN: readyState still reads OPEN, no
  //     close event ever fired, and nothing reconnects. Ask it a question
  //     (ping) and give it a bounded moment to prove it is alive; silence means
  //     replace it. Any inbound frame counts as proof, not just the pong.
  resumeNow(): void {
    if (this.destroyed) return
    this.backoffMs = INITIAL_BACKOFF_MS
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.probeLiveness()
      return
    }
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) return
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.connect()
  }

  private probeLiveness(): void {
    if (this.livenessTimer) return
    const socket = this.ws
    if (!socket) return
    const inboundAtProbe = this.inboundCount
    try {
      socket.send(JSON.stringify({ op: 'ping' }))
    } catch {
      // A throwing send is itself the answer: the socket is gone.
    }
    this.livenessTimer = setTimeout(() => {
      this.livenessTimer = null
      if (this.destroyed || this.ws !== socket) return
      if (this.inboundCount !== inboundAtProbe) return
      // Silent socket. close() drives the normal onclose path, which reconnects
      // at the floored backoff set above.
      try {
        socket.close()
      } catch {
        // Already tearing down; onclose still schedules the retry.
      }
    }, RESUME_PROBE_MS)
  }

  private route(msg: WsMessage): void {
    // ack/pong are protocol-internal; users never see them.
    if (msg.event === 'ack') {
      this.handleAck(msg)
      return
    }
    if (msg.event === 'pong') return
    if (!msg.channel) return
    const handlers = this.subscriptions.get(msg.channel)
    if (!handlers) return
    for (const h of handlers) {
      try {
        h(msg)
      } catch (err) {
        // One bad handler must not break the fan-out for the rest.
        // eslint-disable-next-line no-console
        console.error('[WsClient] handler threw', err)
      }
    }
  }

  private send(op: ClientOp): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false
    try {
      this.ws.send(JSON.stringify(op))
      return true
    } catch {
      // socket may have closed between the readyState check and send;
      // reconnect path will replay the subscribe on next open.
      return false
    }
  }

  private sendChannelOp(
    op: ClientOp & { op: 'subscribe' | 'unsubscribe'; channel: string },
    generation = this.connectionGeneration
  ): void {
    if (!this.send(op)) return
    const queue = this.pendingChannelOps.get(op.channel) ?? []
    queue.push({ op: op.op, generation })
    this.pendingChannelOps.set(op.channel, queue)
  }

  private handleAck(msg: WsMessage): void {
    const data = msg.data as { channel?: unknown; status?: unknown; reason?: unknown } | null
    const channel =
      typeof data?.channel === 'string'
        ? data.channel
        : typeof msg.channel === 'string'
          ? msg.channel
          : ''
    if (!channel) return
    const queue = this.pendingChannelOps.get(channel)
    const pending = queue?.shift()
    if (!pending) return
    if (queue?.length === 0) this.pendingChannelOps.delete(channel)

    if (pending.op !== 'subscribe') return
    if (data?.status !== 'ok') {
      this.rejectSubscriptionWaiters(
        channel,
        pending.generation,
        typeof data?.reason === 'string' && data.reason
          ? data.reason
          : 'WebSocket subscription rejected'
      )
      return
    }
    if (this.subscriptions.has(channel)) {
      this.acknowledgedSubscriptions.set(channel, pending.generation)
      this.resolveSubscriptionWaiters(channel, pending.generation)
    }
    if (
      this.pendingReconnect?.generation === pending.generation &&
      this.pendingReconnect.channels.has(channel)
    ) {
      this.pendingReconnect.channels.delete(channel)
      this.maybeNotifyReconnect()
    }
  }

  private resolveSubscriptionWaiters(channel: string, generation: number): void {
    for (const waiter of this.subscriptionWaiters) {
      if (waiter.channel !== channel || generation < waiter.generation) continue
      this.subscriptionWaiters.delete(waiter)
      waiter.resolve()
    }
  }

  private rejectSubscriptionWaiters(channel: string, generation: number, reason: string): void {
    for (const waiter of this.subscriptionWaiters) {
      if (waiter.channel !== channel || generation < waiter.generation) continue
      this.subscriptionWaiters.delete(waiter)
      waiter.reject(new Error(reason))
    }
  }

  private maybeNotifyReconnect(): void {
    const pending = this.pendingReconnect
    if (!pending || pending.channels.size > 0) return
    this.pendingReconnect = null
    for (const handler of this.reconnectHandlers) {
      try {
        handler()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[WsClient] reconnect handler threw', err)
      }
    }
  }

  // subscribe returns an unsubscribe function. The handler closure is
  // stored directly, so callers must keep a stable reference (the
  // useWsSubscription hook indirects via a ref to make this safe across
  // React re-renders).
  subscribe<T = unknown>(channel: Channel, handler: WsHandler<T>): () => void {
    const wasEmpty = !this.subscriptions.has(channel)
    let set = this.subscriptions.get(channel)
    if (!set) {
      set = new Set()
      this.subscriptions.set(channel, set)
    }
    set.add(handler as WsHandler)
    if (wasEmpty) {
      // First subscriber for this channel → tell the server.
      this.sendChannelOp({ op: 'subscribe', channel })
    }
    return () => {
      const s = this.subscriptions.get(channel)
      if (!s) return
      s.delete(handler as WsHandler)
      if (s.size === 0) {
        this.subscriptions.delete(channel)
        this.sendChannelOp({ op: 'unsubscribe', channel })
        this.pendingReconnect?.channels.delete(channel)
        this.maybeNotifyReconnect()
      }
    }
  }

  // Resolves only after the server ACKs this channel on the current socket.
  // If the socket drops first, the waiter follows the replayed subscribe onto
  // the next connection generation.
  waitForSubscription(channel: Channel): Promise<void> {
    if (!this.subscriptions.has(channel)) {
      return Promise.reject(new Error(`WebSocket channel is not subscribed: ${channel}`))
    }
    const generation =
      this.status === 'open' ? this.connectionGeneration : this.connectionGeneration + 1
    if ((this.acknowledgedSubscriptions.get(channel) ?? 0) >= generation) {
      return Promise.resolve()
    }
    return new Promise<void>((resolve, reject) => {
      this.subscriptionWaiters.add({ channel, generation, resolve, reject })
    })
  }

  destroy(): void {
    this.destroyed = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.livenessTimer) {
      clearTimeout(this.livenessTimer)
      this.livenessTimer = null
    }
    if (this.ws) {
      const ws = this.ws
      ws.onmessage = null
      ws.onerror = null
      ws.onclose = null
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.onopen = () => {
          try {
            ws.close(1000, 'client destroy')
          } catch {
            // ignore
          }
        }
      } else {
        try {
          ws.close(1000, 'client destroy')
        } catch {
          // ignore
        }
      }
      this.ws = null
    }
    this.subscriptions.clear()
    for (const waiter of this.subscriptionWaiters) {
      waiter.reject(new Error('WebSocket client destroyed before subscription ACK'))
    }
    this.subscriptionWaiters.clear()
    this.pendingChannelOps.clear()
    this.acknowledgedSubscriptions.clear()
    this.pendingReconnect = null
    this.reconnectHandlers.clear()
    this.statusHandlers.clear()
    this.setStatus('closed')
  }
}
